import type { OpenAPI } from "simplydo/interfaces";
import {
  DropdownHeader,
  type DropdownProps,
  Icon,
  Image,
} from "simplydo/react/ui";
import { Dropdown } from "simplydo/react/ui";

import StyledDropdownItem from "components/lib/UI/StyledDropdownItem";

const BusinessProfileDropdownItem = ({
  businessProfile,
  selected,
}: {
  businessProfile: OpenAPI.Schemas["IdeaBusinessProfile"];
  selected?: boolean;
}) => {
  const { name, imageUrl, address = {}, _id } = businessProfile;
  const { country, city } = address;
  return (
    <StyledDropdownItem
      key={_id}
      text={name ?? ""}
      image={
        imageUrl ? (
          <Image
            src={imageUrl}
            style={{
              borderRadius: 2,
              overflow: "hidden",
              height: 28,
              width: 28,
              objectFit: "contain",
            }}
          />
        ) : (
          <Icon name="building" size="large" />
        )
      }
      description={
        city || country
          ? `${city || ""}${city && country ? ", " : ""}${country}`
          : undefined
      }
      selected={selected}
    />
  );
};

type IProfileSelector = {
  selectedBusinessProfile?: OpenAPI.Schemas["IdeaBusinessProfile"];
  isLoading: boolean;
  existingProfiles: OpenAPI.Schemas["IdeaBusinessProfile"][];
  joinableProfiles?: OpenAPI.Schemas["IdeaBusinessProfile"][];
  joinProfile?: (profileId: string) => void;
  selectBusinessProfile: (
    ideaBusinessProfile: OpenAPI.Schemas["IdeaBusinessProfile"],
  ) => void;
  deselectBusinessProfile: () => void;
};

const BusinessProfileSelector = ({
  selectedBusinessProfile,
  isLoading,
  existingProfiles,
  joinableProfiles = [],
  joinProfile,
  selectBusinessProfile,
  deselectBusinessProfile,
}: IProfileSelector) => {
  const handleChange = (_e: React.SyntheticEvent, { value }: DropdownProps) => {
    if (!value) {
      deselectBusinessProfile();
      return;
    }
    const profileId = String(value);
    const joinable = joinableProfiles.find(
      (profile) => profile._id === profileId,
    );
    if (joinable) {
      joinProfile?.(profileId);
      selectBusinessProfile(joinable);
      return;
    }
    const existing = existingProfiles.find(
      (profile) => profile._id === profileId,
    );
    if (existing) {
      selectBusinessProfile(existing);
    }
  };

  return (
    <Dropdown
      selection
      fluid
      clearable
      placeholder="Select an organisation profile.."
      loading={isLoading}
      // @ts-ignore
      text={
        selectedBusinessProfile ? (
          <BusinessProfileDropdownItem
            businessProfile={selectedBusinessProfile}
          />
        ) : null
      }
      value={selectedBusinessProfile?._id}
      onChange={handleChange}
      options={[
        ...(joinableProfiles.length > 0
          ? [
              {
                value: "joinable-header",
                className: "header",
                content: (
                  <DropdownHeader content="Organisations you can join" />
                ),
                disabled: true,
              },
            ]
          : []),
        ...joinableProfiles.map((joinableProfile) => ({
          key: joinableProfile._id,
          value: joinableProfile._id,
          content: (
            <BusinessProfileDropdownItem businessProfile={joinableProfile} />
          ),
        })),
        ...(joinableProfiles.length > 0
          ? [
              {
                value: "existing-header",
                className: "header",
                content: <DropdownHeader content="Your organisations" />,
                disabled: true,
              },
            ]
          : []),
        ...existingProfiles.map((existingProfile) => ({
          key: existingProfile._id,
          value: existingProfile._id,
          content: (
            <BusinessProfileDropdownItem businessProfile={existingProfile} />
          ),
        })),
      ]}
    />
  );
};

export default BusinessProfileSelector;
